import React, { useEffect, useState } from 'react';
import { css } from 'emotion';
import { getZIndex } from '@root/src/web/lib/getZIndex';
import { submitComponentEvent } from '@root/src/web/browser/ophan/ophan';
const containerStyles = css `
	position: fixed;
	bottom: -1px;
	width: 100%;
	${getZIndex('banner')}
`;
const FORCE_BRAZE_ALLOWLIST = [
    'preview.gutools.co.uk',
    'preview.code.dev-gutools.co.uk',
    'localhost',
    'm.thegulocal.com',
];
const getBrazeMetaFromQueryString = () => {
    if (URLSearchParams) {
        const qsArg = 'force-braze-message';
        const params = new URLSearchParams(window.location.search);
        const value = params.get(qsArg);
        if (value) {
            if (!FORCE_BRAZE_ALLOWLIST.includes(window.location.hostname)) {
                // eslint-disable-next-line no-console
                console.log(`${qsArg} is not supported on this domain`);
                return null;
            }
            try {
                const dataFromBraze = JSON.parse(value);
                return {
                    dataFromBraze,
                    logImpressionWithBraze: () => { },
                    logButtonClickWithBraze: () => { },
                };
            }
            catch (e) {
                const error = e;
                // Parsing failed. Log a message and fall through.
                // eslint-disable-next-line no-console
                console.log(`There was an error with ${qsArg}: `, error.message);
            }
        }
    }
    return null;
};
// We can show a Braze banner if:
// - The Braze switch is on
// - We have a Braze API key
// - The user should have support messaging hidden, implying they are a contributor or subscriber
// - We're not on a Glabs paid content page
// - We've got a Braze UUID from the API, given a user's ID Creds
// - The user has given Consent via CCPA or TCFV2
// - The Braze websdk appboy initialisation does not throw an error
// - The Braze app Boy subscription to in app message returns meta info
// OR
// - The force-braze-message query string arg is passed
export const canShow = async (brazeMessagesPromise) => {
    const forcedBrazeMeta = getBrazeMetaFromQueryString();
    if (forcedBrazeMeta) {
        return {
            result: true,
            meta: forcedBrazeMeta,
        };
    }
    try {
        const brazeMessages = await brazeMessagesPromise;
        const message = await brazeMessages.getMessageForBanner();
        const logButtonClickWithBraze = (internalButtonId) => {
            message.logButtonClick(internalButtonId);
        };
        const logImpressionWithBraze = () => {
            // Log the impression with Braze
            message.logImpression();
        };
        const meta = {
            dataFromBraze: message.extras,
            logImpressionWithBraze,
            logButtonClickWithBraze,
        };
        return { result: true, meta };
    }
    catch (e) {
        return { result: false };
    }
};
const BrazeBannerWithSatisfiedDependencies = ({ BrazeComponent, meta, }) => {
    useEffect(() => {
        // Log the impression with Braze
        meta.logImpressionWithBraze();
        // Log VIEW event with Ophan
        submitComponentEvent({
            component: {
                componentType: 'RETENTION_ENGAGEMENT_BANNER',
                id: meta.dataFromBraze.componentName,
            },
            action: 'VIEW',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement("div", { className: containerStyles },
        React.createElement(BrazeComponent, { logButtonClickWithBraze: meta.logButtonClickWithBraze, submitComponentEvent: submitComponentEvent, componentName: meta.dataFromBraze.componentName, brazeMessageProps: meta.dataFromBraze })));
};
export const BrazeBanner = ({ meta }) => {
    const [BrazeComponent, setBrazeComponent] = useState();
    useEffect(() => {
        import(
        /* webpackChunkName: "guardian-braze-components" */ '@guardian/braze-components')
            .then((module) => {
            setBrazeComponent(() => module.BrazeMessage);
        })
            .catch((error) => window.guardian.modules.sentry.reportError(error, 'braze-banner'));
    }, []);
    return (React.createElement(React.Fragment, null, BrazeComponent ? (React.createElement(BrazeBannerWithSatisfiedDependencies, { BrazeComponent: BrazeComponent, meta: meta })) : (React.createElement("div", null))));
};
